/**
 * Stream lifecycle: detail, create/update, operators, LinkedIn URLs, statistics, and related lookups.
 *
 * Where: `StreamDetails`, `StreamCreateModal`, stream tabs, and team stream cards.
 * Uses: `apiClient`; exports `StreamDetails` for statistics components.
 * Contract: GET `/streams/` (list), GET `/streams/:id/` (detail → `response.data.output`), GET `/streams/statistics/`, GET `/streams/:id/prospect-pipelines/`; POST `/streams/` create; PATCH `/streams/:id/update/` (incl. soft-delete status); prospect-pipeline actions under `/prospect-pipelines/:id/` (delete, stop, retry, run-now); POST `/streams/:id/prospect-pipelines/retry-failed/`, `/streams/:id/withdraw-pending-invites/`. List/detail/statistics return shapes differ — follow each method’s return (`response.data` vs `.output`).
 * Behavior: client-side helpers `getStepCategories`, `getActivityFilters`, sort option lists are **local only** (not HTTP).
 */

import { apiClient } from './client';

export interface StreamDetails {
  key: string;
  name: string;
  status: string;
  created_at: string;
  notes?: string;
  ai_theme_plot?: string | null;
  start_date?: string;
  end_date?: string;
  team?: {
    key: string;
    name: string;
  } | null;
  creator?: {
    key: string;
    name: string;
    email: string;
    is_unipile_connected?: boolean;
    premium_features?: string[];
  };
  operator?: {
    key: string;
    name: string;
    email: string;
    is_unipile_connected?: boolean;
    premium_features?: string[];
  };
    statistics?: {
    total_leads?: number;
    scheduled_meetings?: number;
    scheduled_meetings_since_yesterday?: number;
    acceptance_rate?: number;
    response_rate?: number;
    sent_invites?: number;
    sent_invites_yesterday?: number;
    sent_invites_day_before?: number;
    sent_messages?: number;
    accepted_connections?: number;
    waiting_connections?: number;
    messages_received?: number;
    failed_jobs?: number;
    failed_jobs_yesterday?: number;
      prospect_estimation_days?: number;
      progress_sections?: Array<{
        category: string;
        count: number;
        color?: string;
      }>;
    accepted_connections_yesterday?: number;
    accepted_connections_day_before?: number;
    accepted_meetings_yesterday?: number;
    accepted_meetings_day_before?: number;
    positive_responses_yesterday?: number;
    positive_responses_day_before?: number;
    number_of_streams?: number;
    number_of_operators?: number;
    meeting_accepted_count?: number;
  };
}

export interface ProspectPipeline {
  key: string;
  name: string;
  status: string;
  created_at: string;
  step_categories: string[];
  available_at?: string | null;
  activity_summary?: string;
  chat_history_summary?: string;
  prospect: {
    key: string;
    first_name: string;
    last_name: string;
    full_name: string;
    title?: string;
    location?: string;
    profile_image?: string | {
      url?: string;
    };
  };
  step?: {
    key: string;
    name: string;
    categories: string[];
  };
}

export interface ProspectPipelineResponse {
  output: {
    items: ProspectPipeline[];
    next_page_token?: string;
    previous_page_token?: string;
    total_results: number;
  };
}

export interface ProspectPipelineFilters {
  page_token?: string;
  max_results?: number;
  step_categories?: string[];
  search?: string;
  status?: string;
  sort_field?: string;
  sort_direction?: 'asc' | 'desc';
  activity?: string;
  happened_after?: string;
  happened_before?: string;
}

export const streamApi = {
  /**
   * Get streams with optional filters and sorting
   */
  getStreams: async (params?: {
    team_uuid?: string;
    add_statistics?: boolean;
    status?: string;
    search?: string;
    sort_field?: string;
    sort_direction?: 'asc' | 'desc';
    page_token?: string;
    max_results?: number;
    operator_uuid?: string; // Filter by operator
    creator_uuid?: string; // Filter by creator
    start_date_from?: string; // Filter by start_date >= this date
    start_date_to?: string; // Filter by start_date <= this date
    end_date_from?: string; // Filter by end_date >= this date
    end_date_to?: string; // Filter by end_date <= this date
  }): Promise<{ output: { items: StreamDetails[]; next_page_token?: string; previous_page_token?: string; total_results?: number } }> => {
    const response = await apiClient.get<any>('/streams/', { params });
    return response.data;
  },

  /**
   * Get stream details with statistics
   */
  getStreamDetails: async (streamId: string, addStatistics: boolean = true): Promise<StreamDetails> => {
    const response = await apiClient.get<{output: StreamDetails}>(`/streams/${streamId}/`, {
      params: { add_statistics: addStatistics }
    });
    return response.data.output;
  },

  /**
   * Get aggregated statistics for all streams matching the provided filters
   */
  getStreamsStatistics: async (params?: {
    team_uuid?: string;
    status?: string;
    search?: string;
    operator_uuid?: string;
    start_date_from?: string; // Filter by start_date >= this date
    start_date_to?: string; // Filter by start_date <= this date
    end_date_from?: string; // Filter by end_date >= this date
    end_date_to?: string; // Filter by end_date <= this date
  }): Promise<{ output: StreamDetails['statistics'] }> => {
    const response = await apiClient.get<{output: StreamDetails['statistics']}>('/streams/statistics/', { params });
    return response.data;
  },

  /**
   * Get prospect pipelines for a stream
   */
  getProspectPipelines: async (streamId: string, filters?: ProspectPipelineFilters): Promise<ProspectPipelineResponse> => {
    const response = await apiClient.get<ProspectPipelineResponse>(
      `/streams/${streamId}/prospect-pipelines/`,
      { params: filters }
    );
    return response.data;
  },

  /**
   * Remove prospect pipeline
   */
  removeProspectPipeline: async (prospectPipelineId: string): Promise<void> => {
    await apiClient.delete(`/prospect-pipelines/${prospectPipelineId}/`);
  },

  /**
   * Manually stop a prospect pipeline
   */
  stopProspectPipeline: async (prospectPipelineId: string): Promise<void> => {
    await apiClient.post(`/prospect-pipelines/${prospectPipelineId}/stop/`);
  },

  /**
   * Manually retry a failed prospect pipeline
   */
  retryProspectPipeline: async (prospectPipelineId: string): Promise<void> => {
    await apiClient.post(`/prospect-pipelines/${prospectPipelineId}/retry/`);
  },

  /**
   * Manually queue a prospect pipeline to run immediately
   */
  runProspectPipelineNow: async (prospectPipelineId: string): Promise<void> => {
    await apiClient.post(`/prospect-pipelines/${prospectPipelineId}/run-now/`);
  },

  /**
   * Retry all failed prospect pipelines for a stream
   */
  retryStreamFailedProspectPipelines: async (streamId: string): Promise<{ message: string; count: number }> => {
    const response = await apiClient.post<{ output: { message: string; count: number } }>(
      `/streams/${streamId}/prospect-pipelines/retry-failed/`,
    );
    return response.data.output;
  },

  /**
   * Create a job to withdraw pending invites for a stream
   */
  withdrawPendingInvites: async (streamId: string): Promise<{ output: any }> => {
    const response = await apiClient.post<{ output: any }>(
      `/streams/${streamId}/withdraw-pending-invites/`,
    );
    return response.data;
  },

  /**
   * Delete stream
   */
  deleteStream: async (streamId: string): Promise<void> => {
    // Note: This would need to be implemented in the backend
    // For now, we'll use the existing update endpoint to mark as deleted
    await apiClient.patch(`/streams/${streamId}/update/`, { status: 'DELETED' });
  },

  /**
   * Create stream
   */
  createStream: async (data: {
    team_uuid?: string; // Made optional
    name: string;
    status?: string;
    notes?: string;
    operator_uuid?: string; // Added
    creator_uuid?: string; // Added
    start_date?: string; // Start date (YYYY-MM-DD)
    end_date?: string; // End date (YYYY-MM-DD)
    messages_template?: string; // raw string
    // hidden/advanced optional fields
    persona?: string;
    prospect_application?: string;
    prospect_brief?: string;
    prospect_operation?: string;
    company_application?: string;
    company_brief?: string;
    company_operation?: string;
    post_filter_criteria?: string;
    territory?: string;
    mls_topic?: string;
    sales_nav_urls?: string[];
    company_names?: string[];
    mls_filters?: any;
    mls_segment_ids?: string[];
    mls_type?: string;
    google_sheet_urls?: string[];
    ai_theme_plot?: string;
  }) => {
    const response = await apiClient.post<any>('/streams/', data);
    return response.data;
  },

  /**
   * Get step categories for filtering
   */
  getStepCategories: () => [
    'Check Connection',
    'Send Connection Request',
    'Waiting for Connection Response',
    'Accepted Connection',
    'Withdraw Connection',
    'Sent Schedule Meeting',
    'Message Received',
    'End Of Sequence',
    'Already Connected',
    'Cannot Invite',
  ],

  getActivityFilters: () => [
    'Send Connection Request',
    'Withdraw Connection',
    'Sent Schedule Meeting',
    'Connection Accepted',
    'Message Received',
  ],

  /** Sort options for leads: name, action_date, created_at, plus all activity names */
  getLeadsSortOptions: () => [
    { value: 'created_at', label: 'Created At' },
    { value: 'name', label: 'Name' },
    { value: 'action_date', label: 'Action Date' },
    { value: 'Send Connection Request', label: 'Send Connection Request' },
    { value: 'Withdraw Connection', label: 'Withdraw Connection' },
    { value: 'Sent Schedule Meeting', label: 'Sent Schedule Meeting' },
    { value: 'Connection Accepted', label: 'Connection Accepted' },
    { value: 'Message Received', label: 'Message Received' },
  ],

  /** Sort options for stream leads: old fields (available_at, status, created_at) + name, action_date, activity names */
  getStreamLeadsSortOptions: () => [
    { value: 'available_at', label: 'Available At' },
    { value: 'status', label: 'Status' },
    { value: 'created_at', label: 'Created At' },
    { value: 'name', label: 'Name' },
    { value: 'action_date', label: 'Action Date' },
    { value: 'Send Connection Request', label: 'Send Connection Request' },
    { value: 'Withdraw Connection', label: 'Withdraw Connection' },
    { value: 'Sent Schedule Meeting', label: 'Sent Schedule Meeting' },
    { value: 'Connection Accepted', label: 'Connection Accepted' },
    { value: 'Message Received', label: 'Message Received' },
  ],
};
